import AiStarIcon from "@redotech/redo-icons/ai-star.svg";
import SearchIcon from "@redotech/redo-icons/search-sm.svg";
import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { RedoBadge } from "../../../arbiter-components/badge/redo-badge";
import { RedoEmptyState } from "../../../arbiter-components/empty-state/redo-empty-state";
import { Flex } from "../../../flex";
import { Text } from "../../../text";
import {
  CommandPaletteItem,
  useCommandPalette,
} from "../../contexts/command-palette-context";
import * as styles from "./command-palette-search-view.module.css";
import { HighlightedText } from "./highlighted-text";

export interface CommandPaletteSearchViewProps {
  searchValue: string;
}

interface SearchItemProps {
  item: CommandPaletteItem;
  isSelected: boolean;
}

const SearchItem = memo(function SearchItem({
  item,
  isSelected,
}: SearchItemProps) {
  const itemRef = useRef<HTMLDivElement>(null);
  const { performAction, state } = useCommandPalette();

  useEffect(() => {
    if (isSelected && itemRef.current) {
      itemRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    }
  }, [isSelected]);

  const handleClick = () => {
    performAction?.(item);
  };

  return (
    <Flex
      align="center"
      borderRadius="md"
      className={`${styles.resultItem} ${isSelected ? styles.selected : ""}`}
      cursor="pointer"
      justify="space-between"
      mx="md"
      onClick={handleClick}
      p="md"
      ref={itemRef}
    >
      <Flex align="center" gap="lg" minW="none" overflow="hidden">
        {item.icon && (
          <Flex className={styles.resultIcon} shrink={0}>
            {item.icon}
          </Flex>
        )}
        <Flex grow={1} minW="none">
          <Flex align="center" gap="sm" minW="none">
            <Text
              as="span"
              className={styles.resultTitle}
              fontSize="xs"
              minW="none"
              overflow="hidden"
              textColor="primary"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              <HighlightedText query={state.searchValue} text={item.title} />
            </Text>
            {item.tags &&
              item.tags.map((tag) => (
                <RedoBadge
                  color={tag.color}
                  key={tag.text}
                  size="sm"
                  text={tag.text}
                />
              ))}
            {item.subtitle && (
              <Text
                as="span"
                className={styles.resultSubtitle}
                fontSize="xs"
                grow={1}
                minW="none"
                overflow="hidden"
                textColor="secondary"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                <HighlightedText
                  query={state.searchValue}
                  text={item.subtitle}
                />
              </Text>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
});

interface SearchGroupProps {
  category: string;
  items: CommandPaletteItem[];
  selectedIndex: number;
  startIndex: number;
  showCategoryHeader?: boolean;
}

const SearchGroup = memo(function SearchGroup({
  category,
  items,
  selectedIndex,
  startIndex,
  showCategoryHeader = true,
}: SearchGroupProps) {
  const categoryHeader = useMemo(() => {
    return category.charAt(0).toUpperCase() + category.slice(1);
  }, [category]);

  return (
    <Flex className={styles.searchGroup} dir="column">
      {showCategoryHeader && (
        <Flex align="center" pb="md" px="xl">
          <Text as="span" fontSize="xs" textColor="secondary">
            {categoryHeader}
          </Text>
        </Flex>
      )}
      <Flex dir="column" gap="xxs">
        {items.map((item, index) => (
          <SearchItem
            isSelected={startIndex + index === selectedIndex}
            item={item}
            key={item.id}
          />
        ))}
      </Flex>
    </Flex>
  );
});

const AskAiRow = memo(function AskAiRow({
  searchValue,
}: {
  searchValue: string;
}) {
  const { state, actions } = useCommandPalette();
  const rowRef = useRef<HTMLDivElement>(null);
  const isSelected = state.selectedIndex === -1;

  const handleClick = useCallback(() => {
    if (state.onAskAI) {
      state.onAskAI(state.searchValue);
      actions.closePalette();
    }
  }, [state, actions]);

  useEffect(() => {
    if (isSelected && rowRef.current) {
      rowRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    }
  }, [isSelected]);

  if (!state.onAskAI || !searchValue.trim()) {
    return null;
  }

  return (
    <Flex
      align="center"
      borderRadius="md"
      className={`${styles.resultItem} ${isSelected ? styles.selected : ""}`}
      cursor="pointer"
      justify="space-between"
      mx="md"
      onClick={handleClick}
      p="md"
      ref={rowRef}
    >
      <Flex align="center" dir="row" gap="md" overflow="hidden">
        <Flex shrink={0}>
          <AiStarIcon className={styles.askAiIcon} />
        </Flex>
        <Text
          as="span"
          className={styles.resultTitle}
          fontSize="xs"
          minW="none"
          overflow="hidden"
          textColor="primary"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          Ask us, &ldquo;
          <Text as="span" fontWeight="medium">
            {searchValue}
          </Text>
          &rdquo;
        </Text>
      </Flex>
      <Flex shrink={0}>
        <RedoBadge color="orange" size="sm" text="⇧ + Enter" />
      </Flex>
    </Flex>
  );
});

export const CommandPaletteSearchView = memo(function CommandPaletteSearchView({
  searchValue,
}: CommandPaletteSearchViewProps) {
  const { state } = useCommandPalette();
  const { searchResults, selectedIndex } = state;
  const hasActiveFilters = state.filters.some((filter) => filter.enabled);

  const groupedResults = useMemo(() => {
    const groups = new Map<string, CommandPaletteItem[]>();

    for (const item of searchResults) {
      const category = item.category;
      if (!groups.has(category)) {
        groups.set(category, []);
      }
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      groups.get(category)!.push(item);
    }

    return Array.from(groups.entries()).map(([category, items]) => ({
      category,
      items,
    }));
  }, [searchResults]);

  const shouldShowCategoryHeaders = useMemo(() => {
    return groupedResults.length > 1;
  }, [groupedResults]);

  if (!searchValue.trim() && !hasActiveFilters && searchResults.length === 0) {
    return (
      <Flex className={styles.container}>
        <RedoEmptyState
          Icon={SearchIcon}
          iconColor="gray"
          subtext="Type to search for commands, shortcuts, and more."
          text="Start searching"
          useNonArbiterPadding
        />
      </Flex>
    );
  }

  if (searchResults.length === 0) {
    return (
      <Flex className={styles.container}>
        <RedoEmptyState
          Icon={SearchIcon}
          iconColor="gray"
          subtext={`We couldn't find anything matching "${searchValue}". Try different keywords.`}
          text="No results found"
          useNonArbiterPadding
        />
      </Flex>
    );
  }

  let currentIndex = 0;

  return (
    <Flex className={styles.container} dir="column">
      <div className={styles.resultsList}>
        <AskAiRow searchValue={searchValue} />
        {groupedResults.map(({ category, items }, groupIndex) => {
          const startIndex = currentIndex;
          const group = (
            <div key={category}>
              <SearchGroup
                category={category}
                items={items}
                selectedIndex={selectedIndex}
                showCategoryHeader={shouldShowCategoryHeaders}
                startIndex={startIndex}
              />
              {groupIndex < groupedResults.length - 1 && (
                <div className={styles.groupSeparator} />
              )}
            </div>
          );
          currentIndex += items.length;
          return group;
        })}
      </div>
    </Flex>
  );
});
